import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Visibility, VisibilityOff, Add, Remove } from '@mui/icons-material';

import BusinessHours from '../../../../input/BusinessHours/BusinessHours';
import Button from '../../../../input/Button';
import { useStyles } from './ManageHours.styles';
import { Loading } from '../../../../layout/Loading';
import {
  editManageHoursETL,
  editManageMediaETL,
} from '../../../../../utils/ExtractTransformLoad/EditRestaurantETL';
import {
  updateSettings,
  requestTempClosingHours,
} from '../../../../../redux/ducks/RestaurantDucks';
import formatPhoneNumber from '../../../../../utils/FormatUtils/formatPhoneNumber';
import TempClosingHours from '../../../../input/TempClosingHours/TempClosingHours';

export function ManageHours() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const restaurantSettings = useSelector((state) => state.restaurant.settings);
  const rawSettings = useSelector((state) => state.restaurant.rawSettings);
  const isLoading = useSelector((state) => state.restaurant.isLoading);
  const restaurantId = useSelector(
    (state) => state?.restaurant?.rawSettings?.id
  );
  const [prepTime, setPrepTime] = useState(
    Math.round((rawSettings?.config?.current_preparation_time ?? 1800) / 60)
  );
  const [data, setData] = useState({});
  const [deliverySame, setDeliverySame] = useState(false);
  const [takeoutSame, setTakeoutSame] = useState(false);

  useEffect(() => {
    if (restaurantId) {
      dispatch(requestTempClosingHours({ restaurant: restaurantId }));
    }
  }, [restaurantId, dispatch]);

  useEffect(() => {
    if (rawSettings?.config?.current_preparation_time != null) {
      setPrepTime(Math.round(rawSettings.config.current_preparation_time / 60));
    }
  }, [rawSettings]);

  useEffect(() => {
    if (restaurantSettings) {
      setData({
        business: { ...restaurantSettings.business },
        delivery: { ...restaurantSettings.delivery },
        takeout: { ...restaurantSettings.takeout },
      });
      setTakeoutSame(!!restaurantSettings.takeout.same);
      setDeliverySame(!!restaurantSettings.delivery.same);
    }
  }, [restaurantSettings]);

  const handlePrepTimeSave = useCallback(() => {
    const {
      id,
      config,
      name,
      description,
      address,
      category,
      tags,
      schedule,
      text,
      phone,
      media,
    } = rawSettings;

    if (id)
      dispatch(
        updateSettings({
          id,
          settings: {
            name,
            description,
            address,
            category,
            tags,
            schedule,
            text,
            media,
            ...editManageMediaETL(media),
            ...editManageHoursETL(data),
            phone: formatPhoneNumber(phone),
            config: {
              costs_food_ratio: config.costs_food_ratio,
              current_preparation_time: prepTime * 60,
            },
          },
        })
      );
  }, [prepTime, data, dispatch, rawSettings]);

  const handleCancel = useCallback(() => {
    if (restaurantSettings) {
      setData({
        business: { ...restaurantSettings.business },
        delivery: { ...restaurantSettings.delivery },
        takeout: { ...restaurantSettings.takeout },
      });
      setTakeoutSame(!!restaurantSettings.takeout.same);
      setDeliverySame(!!restaurantSettings.delivery.same);
    }
  }, [restaurantSettings]);

  const handleSave = useCallback(async () => {
    const {
      id,
      config,
      name,
      description,
      address,
      category,
      tags,
      schedule,
      text,
      phone,
      media,
    } = rawSettings;

    const newData = {
      name,
      description,
      address,
      category,
      tags,
      schedule,
      text,
      media,
      ...editManageMediaETL(media),
      ...editManageHoursETL(data),
      phone: formatPhoneNumber(phone),
      config: {
        costs_food_ratio: config.costs_food_ratio,
        current_preparation_time: config.current_preparation_time,
      },
    };

    if (id)
      dispatch(
        updateSettings({
          id,
          settings: newData,
        })
      );
  }, [data, dispatch, rawSettings]);

  const [tempHoursCollapsed, setTempHoursCollapsed] = useState(false);
  const [regularHoursCollapsed, setRegularHoursCollapsed] = useState(true);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h1" className={classes.pageTitle}>
          Manage Hours
        </Typography>
      </Box>
      {rawSettings && (
        <Box className={classes.prepTimeSection}>
          <Box>
            <Typography variant="h3">Prep Time</Typography>
            <Typography variant="h5" style={{ color: '#888', marginTop: '4px' }}>
              Time to prepare an order
            </Typography>
          </Box>
          <Box className={classes.prepTimeStepper}>
            <IconButton
              onClick={() => setPrepTime((v) => Math.max(15, v - 5))}
              disabled={prepTime <= 15}
              size="small"
            >
              <Remove />
            </IconButton>
            <Typography variant="h3" className={classes.prepTimeValue}>
              {prepTime} min
            </Typography>
            <IconButton
              onClick={() => setPrepTime((v) => Math.min(120, v + 5))}
              disabled={prepTime >= 120}
              size="small"
            >
              <Add />
            </IconButton>
            <Button
              variant="contained"
              disabled={
                prepTime ===
                Math.round(
                  (rawSettings?.config?.current_preparation_time ?? 1800) / 60
                )
              }
              onClick={handlePrepTimeSave}
              className={classes.prepTimeSaveButton}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}
      <Box className={classes.hoursSection}>
        <Box className={classes.hoursSectionHeader}>
          <Box className={classes.hoursSectionTitle}>
            <Typography variant="h3">Temporary Closing Hours</Typography>
          </Box>
          <Box
            data-test-id="WkP0S-x6FTF3sz49xBqv4"
            className={classes.hoursSectionAction}
            onClick={() => setTempHoursCollapsed(!tempHoursCollapsed)}
          >
            <Typography variant="h5">
              {!tempHoursCollapsed && (
                <VisibilityOff size={22} style={{ color: '#0058FF' }} />
              )}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.hoursSectionBody}>
          <TempClosingHours
            collapsed={tempHoursCollapsed}
            onShow={() => setTempHoursCollapsed(!tempHoursCollapsed)}
          />
        </Box>
      </Box>
      <Box className={classes.hoursSection}>
        <Box className={classes.hoursSectionHeader}>
          <Box className={classes.hoursSectionTitle}>
            <Typography variant="h3">Regular Hours</Typography>
          </Box>
          <Box
            data-test-id="FOstngKHrW2Ks7l_seluD"
            className={classes.hoursSectionAction}
            onClick={() => setRegularHoursCollapsed(!regularHoursCollapsed)}
          >
            <Typography variant="h5">
              {!regularHoursCollapsed && (
                <VisibilityOff size={22} style={{ color: '#0058FF' }} />
              )}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.hoursSectionBody}>
          <BusinessHours
            data={data}
            onChange={setData}
            deliveryState={[deliverySame, setDeliverySame]}
            takeoutState={[takeoutSame, setTakeoutSame]}
            collapsed={regularHoursCollapsed}
            onShow={() => setRegularHoursCollapsed(!regularHoursCollapsed)}
            handleSave={handleSave}
            handleCancel={handleCancel}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default ManageHours;
